import React from "react";
import { Navigate, NavLink, Outlet, useLocation } from "react-router-dom";
import { useAuth, hasRole } from "@/context/AuthContext";
import {
  LayoutDashboard, BookOpen, Layers, Newspaper, Compass, Video, CalendarDays,
  Image, Home as HomeIcon, Handshake, Contact, Search, FileText, Users2, ScrollText,
  Languages, LogOut, ShieldCheck, ClipboardList,
} from "lucide-react";
import { Toaster } from "sonner";

const NAV = [
  { to: "/admin", label: "Dashboard", icon: LayoutDashboard, roles: ["owner", "editor", "reviewer"], end: true },
  { to: "/admin/books", label: "Books", icon: BookOpen, roles: ["owner", "editor", "reviewer"] },
  { to: "/admin/reservations", label: "Reservations", icon: ClipboardList, roles: ["owner", "editor", "reviewer"] },
  { to: "/admin/submissions", label: "Submissions", icon: Contact, roles: ["owner", "editor", "reviewer"] },
  { to: "/admin/content/articles", label: "Articles", icon: Newspaper, roles: ["owner", "editor", "reviewer"] },
  { to: "/admin/content/research_themes", label: "Research Themes", icon: Compass, roles: ["owner", "editor", "reviewer"] },
  { to: "/admin/content/videos", label: "Videos", icon: Video, roles: ["owner", "editor", "reviewer"] },
  { to: "/admin/content/events", label: "Events", icon: CalendarDays, roles: ["owner", "editor", "reviewer"] },
  { to: "/admin/content/series", label: "Series", icon: Layers, roles: ["owner", "editor"] },
  { to: "/admin/content/authors", label: "Authors & Contributors", icon: Users2, roles: ["owner", "editor"] },
  { to: "/admin/media", label: "Media Library", icon: Image, roles: ["owner", "editor", "reviewer"] },
  { to: "/admin/homepage", label: "Homepage", icon: HomeIcon, roles: ["owner", "editor"] },
  { to: "/admin/about", label: "About Page", icon: Users2, roles: ["owner", "editor"] },
  { to: "/admin/settings", label: "Site Settings", icon: ShieldCheck, roles: ["owner", "editor"] },
  { to: "/admin/translations", label: "Translations", icon: Languages, roles: ["owner", "editor"] },
  { to: "/admin/content/legal_pages", label: "Legal Pages", icon: ScrollText, roles: ["owner", "editor"] },
  { to: "/admin/activity", label: "Activity History", icon: FileText, roles: ["owner", "editor", "reviewer"] },
  { to: "/admin/staff", label: "Staff & Roles", icon: ShieldCheck, roles: ["owner"] },
];

export default function AdminLayout() {
  const { user, logout } = useAuth();
  const location = useLocation();

  if (user === undefined) {
    return <div className="min-h-screen flex items-center justify-center" style={{ background: "var(--gi-ivory)" }} data-testid="admin-loading">
      <div className="font-heading text-xl" style={{ color: "var(--gi-maroon)" }}>Checking session…</div>
    </div>;
  }
  if (!user) {
    return <Navigate to="/admin/login" replace state={{ from: location }} />;
  }

  return (
    <div className="min-h-screen flex" style={{ background: "var(--gi-ivory-2)" }} data-testid="admin-layout">
      <aside className="w-72 border-r hidden lg:flex flex-col" style={{ borderColor: "var(--gi-border)", background: "#FFFFFF" }} data-testid="admin-sidebar">
        <div className="p-6 border-b" style={{ borderColor: "var(--gi-border)" }}>
          <div className="font-accent text-[10px]" style={{ color: "var(--gi-gold)" }}>Editorial Desk</div>
          <div className="mt-1 font-heading text-lg" style={{ color: "var(--gi-maroon)" }}>Glories of India</div>
        </div>
        <nav className="flex-1 overflow-y-auto py-2">
          {NAV.filter(n => hasRole(user, ...n.roles)).map(n => (
            <NavLink
              key={n.to}
              to={n.to}
              end={n.end}
              className={({ isActive }) => `flex items-center gap-3 px-6 py-2.5 text-sm transition-colors ${isActive ? "bg-[var(--gi-ivory-2)] text-[var(--gi-maroon)] border-l-2 border-[var(--gi-maroon)]" : "text-[var(--gi-charcoal-2)] hover:bg-[var(--gi-ivory-2)] border-l-2 border-transparent"}`}
              data-testid={`admin-nav-${n.label.toLowerCase().replace(/[^a-z0-9]+/g, "-")}`}
            >
              <n.icon size={14} />
              <span>{n.label}</span>
            </NavLink>
          ))}
        </nav>
        <div className="p-6 border-t" style={{ borderColor: "var(--gi-border)" }}>
          <div className="text-sm font-medium" style={{ color: "var(--gi-charcoal)" }} data-testid="admin-current-user-name">{user.name}</div>
          <div className="text-xs mt-1" style={{ color: "var(--gi-muted)" }} data-testid="admin-current-user-role">{user.email} · {user.role}</div>
          <button onClick={logout} className="btn-ghost mt-4" data-testid="admin-logout-btn"><LogOut size={12} /> Sign out</button>
        </div>
      </aside>

      <main className="flex-1 min-w-0">
        <MobileHeader user={user} logout={logout} />
        <div className="p-6 lg:p-10">
          <Outlet />
        </div>
        <Toaster position="bottom-right" richColors />
      </main>
    </div>
  );
}

const MobileHeader = ({ user, logout }) => (
  <div className="lg:hidden flex items-center justify-between px-6 py-4 border-b bg-white" style={{ borderColor: "var(--gi-border)" }}>
    <div>
      <div className="font-heading text-lg" style={{ color: "var(--gi-maroon)" }}>Editorial Desk</div>
      <div className="text-[11px]" style={{ color: "var(--gi-muted)" }}>{user.email} · {user.role}</div>
    </div>
    <button onClick={logout} className="btn-ghost" data-testid="admin-mobile-logout"><LogOut size={12} /></button>
  </div>
);
